
import { useState, useCallback } from 'react';

interface Service {
  service_id: string;
  service_name: string;
  default_price: number;
  parent_service_id?: string;
}

export function useServiceSelection(
  services: Service[] = [],
  initialSelectedServices: Record<string, boolean> = {},
  initialPriceOverrides: Record<string, number> = {}
) {
  const [selectedServices, setSelectedServices] = useState<Record<string, boolean>>(initialSelectedServices);
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number | null>>(initialPriceOverrides);

  const getChildrenIds = useCallback((serviceId: string): string[] => {
    return services
      .filter(service => service.parent_service_id === serviceId)
      .map(service => service.service_id);
  }, [services]);

  const getParentId = useCallback((serviceId: string): string | undefined => {
    const service = services.find(s => s.service_id === serviceId);
    return service?.parent_service_id;
  }, [services]);

  const isAnyParentSelected = useCallback((serviceId: string): boolean => {
    const parentId = getParentId(serviceId);
    if (!parentId) return true; // No parent means it's a root service
    return selectedServices[parentId] === true ? true : isAnyParentSelected(parentId);
  }, [getParentId, selectedServices]);

  const handleServiceSelection = useCallback((service: Service, selected: boolean): Record<string, boolean> => {
    const newSelections = { ...selectedServices };
    
    // Update the selection of the current service
    newSelections[service.service_id] = selected;
    
    // If unselected, also unselect all children recursively
    if (!selected) {
      const unselectedWithChildren = (id: string) => {
        const childIds = getChildrenIds(id);
        childIds.forEach(childId => {
          newSelections[childId] = false;
          unselectedWithChildren(childId);
        });
      };
      
      unselectedWithChildren(service.service_id);
    }
    
    setSelectedServices(newSelections);
    return newSelections;
  }, [selectedServices, getChildrenIds]);

  const handlePriceChange = useCallback((serviceId: string, value: string): Record<string, number | null> => {
    const newPrices = { ...priceOverrides };
    
    if (value === '') {
      // Explicitly store empty values as null
      newPrices[serviceId] = null;
    } else {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue)) {
        newPrices[serviceId] = parsedValue;
      }
    }
    
    setPriceOverrides(newPrices);
    return newPrices;
  }, [priceOverrides]);

  // Update the function to accept either a Service object or a string
  const canSelectService = useCallback((serviceOrId: Service | string): boolean => {
    const serviceId = typeof serviceOrId === 'string' ? serviceOrId : serviceOrId.service_id;
    return isAnyParentSelected(serviceId);
  }, [isAnyParentSelected]);

  return {
    selectedServices,
    priceOverrides,
    handleServiceSelection,
    handlePriceChange,
    canSelectService
  };
}
